import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  FileText, MessageSquare, Briefcase, Calendar, Clock, 
  User, ChevronDown, ChevronUp, RefreshCw, Eye, LogOut,
  Plus, Search, PenTool, ChevronLeft, Home, CheckCircle, AlertCircle,
  Upload, X, Phone, Paperclip, Video, MessageCircle, Download
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { WhatsAppFloatingIcon } from '../components/WhatsAppButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// الحد الأقصى لحجم الملف (5 ميجابايت)
const MAX_FILE_SIZE = 5 * 1024 * 1024;
// الصيغ المسموح بها
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain'
];

export default function ClientPortal() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeView, setActiveView] = useState('main');
  const [data, setData] = useState(null);
  const [clientRequests, setClientRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [expandedCase, setExpandedCase] = useState(null);
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [caseUpdates, setCaseUpdates] = useState({});
  const [requestUpdates, setRequestUpdates] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  // مراجع رفع الملفات
  const caseFileRef = useRef(null);
  const consultationFileRef = useRef(null);
  const notaryFileRef = useRef(null);

  // نموذج قضية جديدة
  const [caseForm, setCaseForm] = useState({
    case_type: '',
    title: '',
    description: '',
    phone_number: '',
  });
  const [caseFiles, setCaseFiles] = useState([]);

  // نموذج استشارة
  const [consultationForm, setConsultationForm] = useState({
    subject: '',
    message: '',
    phone_number: '',
  });
  const [consultationFiles, setConsultationFiles] = useState([]);

  // نموذج خدمات الموثق
  const [notaryForm, setNotaryForm] = useState({
    service_type: '',
    description: '',
    phone_number: '',
    city_name: '', // اسم المدينة للخدمات العقارية
  });
  const [notaryFiles, setNotaryFiles] = useState([]);

  // الخدمات التي تتطلب اسم المدينة (العقارية)
  const propertyServices = ['إفراغ عقارات', 'رهن عقاري', 'استعلام عقاري'];

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [portalRes, requestsRes] = await Promise.all([
        axios.get(`${API}/client-portal/my-items`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/client-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] }))
      ]);
      setData(portalRes.data);
      setClientRequests(requestsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  };

  const fetchCaseUpdates = async (caseId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/client-portal/case/${caseId}/updates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCaseUpdates(prev => ({ ...prev, [caseId]: response.data.updates }));
    } catch (err) {
      console.error('Error fetching case updates:', err);
    }
  };

  const fetchRequestUpdates = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/client/my-request-updates/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequestUpdates(prev => ({ ...prev, [requestId]: response.data }));
    } catch (err) {
      console.error('Error fetching request updates:', err);
    }
  };

  const toggleCase = (caseId) => {
    if (expandedCase === caseId) {
      setExpandedCase(null);
    } else {
      setExpandedCase(caseId);
      if (!caseUpdates[caseId]) {
        fetchCaseUpdates(caseId);
      }
    }
  };

  const toggleRequest = (requestId) => {
    if (expandedRequest === requestId) {
      setExpandedRequest(null);
    } else {
      setExpandedRequest(requestId);
      if (!requestUpdates[requestId]) {
        fetchRequestUpdates(requestId);
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  // التحقق من صحة رقم الجوال (10 أرقام)
  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^[0-9]{10}$/;
    return phoneRegex.test(phone);
  };

  // التعامل مع رفع الملفات
  const handleFileUpload = (e, setFiles, currentFiles) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    const errors = [];

    files.forEach(file => {
      if (currentFiles.length + validFiles.length >= 5) {
        errors.push(`لا يمكن رفع أكثر من 5 ملفات`);
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: حجم الملف يتجاوز 5 ميجابايت`);
        return;
      }
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        errors.push(`${file.name}: صيغة الملف غير مدعومة`);
        return;
      }
      validFiles.push(file);
    });

    if (errors.length > 0) {
      alert(errors.join('\n'));
    }

    if (validFiles.length > 0) {
      setFiles([...currentFiles, ...validFiles]);
    }
    e.target.value = '';
  };

  // حذف ملف
  const removeFile = (index, setFiles, currentFiles) => {
    setFiles(currentFiles.filter((_, i) => i !== index));
  };

  // تحويل الملفات إلى Base64
  const filesToBase64 = async (files) => {
    const promises = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve({
            name: file.name,
            type: file.type,
            size: file.size,
            data: reader.result.split(',')[1]
          });
        };
        reader.readAsDataURL(file);
      });
    });
    return Promise.all(promises);
  };

  // إرسال قضية جديدة
  const submitNewCase = async (e) => {
    e.preventDefault();
    
    if (!validatePhoneNumber(caseForm.phone_number)) {
      alert('يرجى إدخال رقم جوال صحيح مكون من 10 أرقام');
      return;
    }

    setSubmitLoading(true);
    try {
      const token = localStorage.getItem('token');
      const attachments = await filesToBase64(caseFiles);
      
      await axios.post(`${API}/client-requests`, {
        request_type: 'case',
        ...caseForm,
        attachments,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('تم إرسال طلبك بنجاح! سيتم التواصل معك قريباً');
      setCaseForm({ case_type: '', title: '', description: '', phone_number: '' });
      setCaseFiles([]);
      await fetchData(); // تحديث البيانات أولاً
      setActiveView('inquiry'); // الانتقال لصفحة الاستعلام
    } catch (err) {
      alert('حدث خطأ أثناء إرسال الطلب');
    }
    setSubmitLoading(false);
  };

  // إرسال استشارة
  const submitConsultation = async (e) => {
    e.preventDefault();
    
    if (!validatePhoneNumber(consultationForm.phone_number)) {
      alert('يرجى إدخال رقم جوال صحيح مكون من 10 أرقام');
      return;
    }

    setSubmitLoading(true);
    try {
      const token = localStorage.getItem('token');
      const attachments = await filesToBase64(consultationFiles);
      
      await axios.post(`${API}/consultations`, {
        ...consultationForm,
        attachments,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('تم إرسال طلب الاستشارة بنجاح!');
      setConsultationForm({ subject: '', message: '', phone_number: '' });
      setConsultationFiles([]);
      await fetchData(); // تحديث البيانات أولاً
      setActiveView('inquiry'); // الانتقال لصفحة الاستعلام
    } catch (err) {
      alert('حدث خطأ أثناء إرسال الاستشارة');
    }
    setSubmitLoading(false);
  };

  // إرسال طلب موثق
  const submitNotaryRequest = async (e) => {
    e.preventDefault();
    
    if (!validatePhoneNumber(notaryForm.phone_number)) {
      alert('يرجى إدخال رقم جوال صحيح مكون من 10 أرقام');
      return;
    }

    // التحقق من اسم المدينة للخدمات العقارية
    if (propertyServices.includes(notaryForm.service_type) && !notaryForm.city_name.trim()) {
      alert('يرجى إدخال اسم المدينة التي يقع العقار بها');
      return;
    }

    setSubmitLoading(true);
    try {
      const token = localStorage.getItem('token');
      const attachments = await filesToBase64(notaryFiles);
      
      await axios.post(`${API}/client-requests`, {
        request_type: 'notary',
        ...notaryForm,
        attachments,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('تم إرسال طلب خدمة الموثق بنجاح!');
      setNotaryForm({ service_type: '', description: '', phone_number: '', city_name: '' });
      setNotaryFiles([]);
      await fetchData(); // تحديث البيانات أولاً
      setActiveView('inquiry'); // الانتقال لصفحة الاستعلام
    } catch (err) {
      alert('حدث خطأ أثناء إرسال الطلب');
    }
    setSubmitLoading(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('ar-SA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'active': { label: 'نشطة', class: 'bg-green-100 text-green-700', icon: CheckCircle },
      'pending': { label: 'قيد الانتظار', class: 'bg-yellow-100 text-yellow-700', icon: Clock },
      'completed': { label: 'مكتملة', class: 'bg-blue-100 text-blue-700', icon: CheckCircle },
      'closed': { label: 'مغلقة', class: 'bg-gray-100 text-gray-700', icon: CheckCircle },
      'in_progress': { label: 'قيد المعالجة', class: 'bg-purple-100 text-purple-700', icon: AlertCircle },
      'responded': { label: 'تم الرد', class: 'bg-green-100 text-green-700', icon: CheckCircle },
      // الحالات الجديدة
      'جديد': { label: 'جديد', class: 'bg-blue-100 text-blue-700', icon: Clock },
      'قيد المراجعة': { label: 'قيد المراجعة', class: 'bg-yellow-100 text-yellow-700', icon: Clock },
      'تمت الإحالة للمختص': { label: 'تمت الإحالة للمختص', class: 'bg-green-100 text-green-700', icon: CheckCircle },
      'مكتمل': { label: 'مكتمل', class: 'bg-gray-100 text-gray-700', icon: CheckCircle },
    };
    const statusInfo = statusMap[status] || { label: status || 'جديد', class: 'bg-gray-100 text-gray-700', icon: Clock };
    const StatusIcon = statusInfo.icon;
    return (
      <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-body ${statusInfo.class}`}>
        <StatusIcon className="w-3 h-3" />
        {statusInfo.label}
      </span>
    );
  };

  // تحميل مرفق
  const downloadAttachment = (attachment) => {
    try {
      const byteCharacters = atob(attachment.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: attachment.type });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading attachment:', err);
    }
  };

  // فلترة طلبات العميل
  const filteredClientRequests = clientRequests.filter(req =>
    req.title?.includes(searchQuery) ||
    req.request_number?.includes(searchQuery) ||
    req.case_type?.includes(searchQuery) ||
    req.service_type?.includes(searchQuery) ||
    req.description?.includes(searchQuery)
  );

  const caseRequests = filteredClientRequests.filter(r => r.request_type === 'case');
  const notaryRequests = filteredClientRequests.filter(r => r.request_type === 'notary');

  // أنواع القضايا - المحدثة
  const caseTypes = [
    'قضية جنائية',
    'قضية عامة',
    'قضية تجارية',
    'قضية أحوال شخصية',
    'قضية عمالية',
    'قضية إدارية',
    'قضية عقارية',
    'قضايا اللجان المصرفية والتمويلية',
    'قضايا التنفيذ',
    'أخرى',
  ];

  // خدمات الموثق - المحدثة
  const notaryServices = [
    'إصدار وكالة فردية',
    'إصدار وكالات للشركات والمؤسسات',
    'إفراغ عقارات',
    'رهن عقاري',
    'استعلام عقاري',
  ];

  // الخيارات الرئيسية
  const mainOptions = [
    {
      id: 'new-case',
      title: 'تسجيل قضية جديدة',
      description: 'تقديم طلب قضية جديدة للمكتب',
      icon: Plus,
      color: 'bg-green-500 hover:bg-green-600',
    },
    {
      id: 'inquiry',
      title: 'استعلام عن طلب سابق',
      description: 'متابعة حالة طلباتك السابقة',
      icon: Search,
      color: 'bg-blue-500 hover:bg-blue-600',
    },
    {
      id: 'consultation',
      title: 'طلب استشارة',
      description: 'احصل على استشارة قانونية من محامينا',
      icon: MessageSquare,
      color: 'bg-gold hover:bg-gold-dark',
    },
    {
      id: 'notary',
      title: 'خدمات الموثق',
      description: 'توثيق العقود والمستندات الرسمية',
      icon: PenTool,
      color: 'bg-purple-500 hover:bg-purple-600',
    },
  ];

  // مكون عرض الملفات المرفقة
  const FileAttachments = ({ files, setFiles, fileRef, maxFiles = 5 }) => (
    <div>
      <label className="block text-sm font-medium text-primary mb-2 font-body">
        المرفقات (اختياري - حتى {maxFiles} ملفات، الحد الأقصى 5 ميجابايت لكل ملف)
      </label>
      
      <input
        type="file"
        ref={fileRef}
        onChange={(e) => handleFileUpload(e, setFiles, files)}
        multiple
        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
        className="hidden"
      />
      
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={files.length >= maxFiles}
        className={`w-full p-4 border-2 border-dashed rounded-lg transition-all font-body flex items-center justify-center gap-2 ${
          files.length >= maxFiles 
            ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed' 
            : 'border-gray-300 hover:border-gold hover:bg-gold/5 text-gray-600'
        }`}
      >
        <Upload className="w-5 h-5" />
        <span>{files.length >= maxFiles ? 'تم الوصول للحد الأقصى' : 'اضغط لإرفاق ملفات'}</span>
      </button>
      
      <p className="text-xs text-gray-500 mt-1 font-body">
        الصيغ المدعومة: PDF, Word, Excel, صور (JPG, PNG, GIF), نص
      </p>
      
      {files.length > 0 && (
        <div className="mt-3 space-y-2">
          {files.map((file, index) => (
            <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Paperclip className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-body truncate max-w-[200px]">{file.name}</span>
                <span className="text-xs text-gray-400">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
              </div>
              <button
                type="button"
                onClick={() => removeFile(index, setFiles, files)}
                className="p-1 text-red-500 hover:bg-red-50 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // فلترة البيانات للبحث
  const filteredCases = data?.cases?.filter(c => 
    c.title?.includes(searchQuery) || 
    c.case_number?.includes(searchQuery) ||
    c.description?.includes(searchQuery)
  ) || [];

  const filteredConsultations = [...(data?.consultations || []), ...(data?.guest_consultations || [])].filter(c =>
    c.subject?.includes(searchQuery) ||
    c.message?.includes(searchQuery)
  );

  const filteredAssignments = data?.assignments?.filter(a =>
    a.assignment_number?.includes(searchQuery) ||
    a.instructions?.includes(searchQuery)
  ) || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-gold border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-500 font-body">جاري تحميل بياناتك...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      <header className="bg-primary py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Logo size="md" />
            <div>
              <h1 className="text-xl font-heading font-bold text-white">بوابة العميل</h1>
              <p className="text-sm text-gray-300 font-body">مرحباً {user?.full_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {activeView === 'main' && (
              <button
                onClick={fetchData}
                className="flex items-center gap-2 px-4 py-2 bg-gold/20 text-white rounded-md hover:bg-gold/30 transition-all"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4" />
                تحديث
              </button>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-all"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              خروج
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        {activeView !== 'main' && (
          <button
            onClick={() => setActiveView('main')}
            className="mb-6 flex items-center gap-2 text-primary hover:text-gold transition-colors font-body"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>العودة للقائمة الرئيسية</span>
          </button>
        )}

        {/* =============== Main Menu - 4 Options =============== */}
        {activeView === 'main' && (
          <>
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-heading font-bold text-primary mb-2">
                كيف يمكننا مساعدتك اليوم؟
              </h2>
              <p className="text-gray-600 font-body">اختر الخدمة التي تحتاجها</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
              {mainOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.id}
                    data-testid={`option-${option.id}`}
                    onClick={() => setActiveView(option.id)}
                    className={`${option.color} text-white p-8 rounded-xl shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl text-right`}
                  >
                    <Icon className="w-12 h-12 mb-4" />
                    <h3 className="text-xl font-heading font-bold mb-2">{option.title}</h3>
                    <p className="text-sm font-body opacity-90">{option.description}</p>
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-primary">{data?.summary?.total_cases || 0}</p>
                    <p className="text-xs text-gray-500 font-body">القضايا</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <FileText className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-primary">{data?.summary?.active_cases || 0}</p>
                    <p className="text-xs text-gray-500 font-body">نشطة</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <MessageSquare className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-primary">{data?.summary?.total_consultations || 0}</p>
                    <p className="text-xs text-gray-500 font-body">الاستشارات</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Briefcase className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-primary">{data?.summary?.total_assignments || 0}</p>
                    <p className="text-xs text-gray-500 font-body">المعاملات</p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* =============== New Case Form =============== */}
        {activeView === 'new-case' && (
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-green-100 rounded-lg">
                <Briefcase className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-heading font-bold text-primary">تسجيل قضية جديدة</h3>
                <p className="text-sm text-gray-500 font-body">يرجى تعبئة جميع البيانات المطلوبة</p>
              </div>
            </div>

            <form onSubmit={submitNewCase} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-primary mb-2 font-body">
                  نوع القضية <span className="text-red-500">*</span>
                </label>
                <select
                  data-testid="case-type-select"
                  value={caseForm.case_type}
                  onChange={(e) => setCaseForm({ ...caseForm, case_type: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold outline-none font-body"
                >
                  <option value="">اختر نوع القضية</option>
                  {caseTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2 font-body">
                  عنوان القضية <span className="text-red-500">*</span>
                </label>
                <input
                  data-testid="case-title-input"
                  type="text"
                  value={caseForm.title}
                  onChange={(e) => setCaseForm({ ...caseForm, title: e.target.value })}
                  required
                  placeholder="مثال: قضية مطالبة مالية"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold outline-none font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2 font-body">
                  شرح القضية <span className="text-red-500">*</span>
                </label>
                <textarea
                  data-testid="case-description-input"
                  value={caseForm.description}
                  onChange={(e) => {
                    if (e.target.value.length <= 1000) {
                      setCaseForm({ ...caseForm, description: e.target.value });
                    }
                  }}
                  required
                  rows={5}
                  maxLength={1000}
                  placeholder="اشرح تفاصيل القضية..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold outline-none font-body"
                />
                <p className="text-xs text-gray-500 mt-1 font-body text-left">
                  {caseForm.description.length}/1000 حرف
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2 font-body">
                  رقم الجوال <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    value={caseForm.phone_number}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setCaseForm({ ...caseForm, phone_number: val });
                    }}
                    required
                    placeholder="05XXXXXXXX"
                    maxLength={10}
                    className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold outline-none font-body text-left"
                    dir="ltr"
                    data-testid="case-phone-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1 font-body">
                  {caseForm.phone_number.length}/10 أرقام
                  {caseForm.phone_number.length === 10 && <span className="text-green-600 mr-2">✓</span>}
                </p>
              </div>

              <FileAttachments 
                files={caseFiles}
                setFiles={setCaseFiles}
                fileRef={caseFileRef}
              />

              <button
                type="submit"
                disabled={submitLoading}
                data-testid="submit-case-btn"
                className="w-full py-4 bg-green-500 text-white rounded-lg font-bold hover:bg-green-600 transition-all disabled:opacity-50 font-body"
              >
                {submitLoading ? 'جاري الإرسال...' : 'إرسال الطلب'}
              </button>
            </form>
          </div>
        )}

        {/* =============== Inquiry View =============== */}
        {activeView === 'inquiry' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-4 shadow-lg">
              <div className="flex items-center gap-3">
                <Search className="w-5 h-5 text-gray-400" />
                <input
                  data-testid="search-input"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="ابحث برقم الطلب أو العنوان..."
                  className="flex-1 px-4 py-2 border-0 focus:ring-0 outline-none font-body text-lg"
                />
              </div>
            </div>

            {/* طلبات القضايا الجديدة */}
            {caseRequests.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-lg font-heading font-bold text-primary mb-4 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-green-600" />
                  طلبات القضايا الجديدة ({caseRequests.length})
                </h3>
                <div className="space-y-3">
                  {caseRequests.map((req) => (
                    <div key={req.id} className="bg-sand rounded-lg overflow-hidden">
                      <div 
                        className="p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => toggleRequest(req.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-gold font-semibold font-body">{req.request_number}</span>
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded font-body">
                                {req.case_type}
                              </span>
                            </div>
                            <h4 className="font-heading font-semibold text-primary">{req.title}</h4>
                            <p className="text-xs text-gray-500 mt-1">{formatDateTime(req.created_at)}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(req.status)}
                            {req.attachments?.length > 0 && (
                              <span className="flex items-center gap-1 text-blue-600 text-xs">
                                <Paperclip className="w-3 h-3" />
                                {req.attachments.length}
                              </span>
                            )}
                            {expandedRequest === req.id ? (
                              <ChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {expandedRequest === req.id && (
                        <div className="border-t border-gray-200 p-4 bg-white">
                          <div className="mb-3">
                            <p className="text-sm text-gray-500 mb-1">شرح القضية:</p>
                            <p className="font-body text-sm bg-gray-50 p-3 rounded">{req.description}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                            <div>
                              <span className="text-gray-500">رقم الجوال:</span>
                              <span className="mr-2 font-body" dir="ltr">{req.phone_number || '-'}</span>
                            </div>
                          </div>
                          
                          {/* تحديثات المهمة */}
                          {requestUpdates[req.id] && (
                            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <h5 className="font-semibold text-blue-800 mb-2 flex items-center gap-1">
                                <Eye className="w-4 h-4" />
                                تحديثات من فريق العمل
                              </h5>
                              {requestUpdates[req.id].updates?.length > 0 ? (
                                <div className="space-y-2 max-h-40 overflow-y-auto">
                                  {requestUpdates[req.id].updates.map((update, idx) => (
                                    <div key={idx} className="bg-white p-2 rounded text-sm border border-blue-100">
                                      <p className="font-body">{update.update_text}</p>
                                      <p className="text-xs text-gray-500 mt-1">
                                        {update.updated_by_name} - {formatDateTime(update.created_at)}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-blue-600">{requestUpdates[req.id].message || 'جاري العمل على طلبك'}</p>
                              )}
                              {requestUpdates[req.id].assigned_to?.length > 0 && (
                                <p className="text-xs text-blue-600 mt-2">
                                  المسؤول: {requestUpdates[req.id].assigned_to.join('، ')}
                                </p>
                              )}
                            </div>
                          )}
                          
                          {req.attachments?.length > 0 && (
                            <div className="mt-3">
                              <p className="text-sm text-gray-500 mb-2">المرفقات:</p>
                              <div className="space-y-2">
                                {req.attachments.map((att, idx) => (
                                  <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                    <div className="flex items-center gap-2">
                                      <Paperclip className="w-4 h-4 text-gray-400" />
                                      <span className="text-sm font-body">{att.name}</span>
                                    </div>
                                    <button
                                      onClick={() => downloadAttachment(att)}
                                      className="p-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                                    >
                                      <Download className="w-4 h-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* سجل التحديثات من الإدارة */}
                          {req.status_history?.length > 0 && (
                            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <h5 className="font-semibold text-blue-800 mb-2 flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                سجل تحديثات الطلب
                              </h5>
                              <div className="space-y-2 max-h-40 overflow-y-auto">
                                {req.status_history.map((history, idx) => (
                                  <div key={idx} className="bg-white p-2 rounded text-sm border border-blue-100">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="font-semibold text-primary">{history.status}</span>
                                      <span className="text-xs text-gray-500">{formatDateTime(history.updated_at)}</span>
                                    </div>
                                    {history.note && (
                                      <p className="text-gray-600 font-body">{history.note}</p>
                                    )}
                                    <p className="text-xs text-gray-400 mt-1">بواسطة: {history.updated_by_name}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* طلبات خدمات الموثق */}
            {notaryRequests.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-lg font-heading font-bold text-primary mb-4 flex items-center gap-2">
                  <PenTool className="w-5 h-5 text-purple-600" />
                  طلبات خدمات الموثق ({notaryRequests.length})
                </h3>
                <div className="space-y-3">
                  {notaryRequests.map((req) => (
                    <div key={req.id} className="bg-sand rounded-lg overflow-hidden">
                      <div 
                        className="p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => toggleRequest(req.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-gold font-semibold font-body">{req.request_number}</span>
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded font-body">
                                {req.service_type}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 font-body">{req.description?.substring(0, 100)}...</p>
                            <p className="text-xs text-gray-500 mt-1">{formatDateTime(req.created_at)}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(req.status)}
                            {req.attachments?.length > 0 && (
                              <span className="flex items-center gap-1 text-blue-600 text-xs">
                                <Paperclip className="w-3 h-3" />
                                {req.attachments.length}
                              </span>
                            )}
                            {expandedRequest === req.id ? (
                              <ChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {expandedRequest === req.id && (
                        <div className="border-t border-gray-200 p-4 bg-white">
                          <div className="mb-3">
                            <p className="text-sm text-gray-500 mb-1">تفاصيل الطلب:</p>
                            <p className="font-body text-sm bg-gray-50 p-3 rounded">{req.description}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                            <div>
                              <span className="text-gray-500">رقم الجوال:</span>
                              <span className="mr-2 font-body" dir="ltr">{req.phone_number || '-'}</span>
                            </div>
                            {req.city_name && (
                              <div>
                                <span className="text-gray-500">المدينة:</span>
                                <span className="mr-2 font-body">{req.city_name}</span>
                              </div>
                            )}
                          </div>
                          
                          {/* تحديثات المهمة */}
                          {requestUpdates[req.id] && (
                            <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                              <h5 className="font-semibold text-purple-800 mb-2 flex items-center gap-1">
                                <Eye className="w-4 h-4" />
                                تحديثات من فريق العمل
                              </h5>
                              {requestUpdates[req.id].updates?.length > 0 ? (
                                <div className="space-y-2 max-h-40 overflow-y-auto">
                                  {requestUpdates[req.id].updates.map((update, idx) => (
                                    <div key={idx} className="bg-white p-2 rounded text-sm border border-purple-100">
                                      <p className="font-body">{update.update_text}</p>
                                      <p className="text-xs text-gray-500 mt-1">
                                        {update.updated_by_name} - {formatDateTime(update.created_at)}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-purple-600">{requestUpdates[req.id].message || 'جاري العمل على طلبك'}</p>
                              )}
                              {requestUpdates[req.id].assigned_to?.length > 0 && (
                                <p className="text-xs text-purple-600 mt-2">
                                  المسؤول: {requestUpdates[req.id].assigned_to.join('، ')}
                                </p>
                              )}
                            </div>
                          )}
                          
                          {req.attachments?.length > 0 && (
                            <div className="mt-3">
                              <p className="text-sm text-gray-500 mb-2">المرفقات:</p>
                              <div className="space-y-2">
                                {req.attachments.map((att, idx) => (
                                  <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                    <div className="flex items-center gap-2">
                                      <Paperclip className="w-4 h-4 text-gray-400" />
                                      <span className="text-sm font-body">{att.name}</span>
                                    </div>
                                    <button
                                      onClick={() => downloadAttachment(att)}
                                      className="p-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                                    >
                                      <Download className="w-4 h-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* سجل التحديثات من الإدارة */}
                          {req.status_history?.length > 0 && (
                            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <h5 className="font-semibold text-blue-800 mb-2 flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                سجل تحديثات الطلب
                              </h5>
                              <div className="space-y-2 max-h-40 overflow-y-auto">
                                {req.status_history.map((history, idx) => (
                                  <div key={idx} className="bg-white p-2 rounded text-sm border border-blue-100">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="font-semibold text-primary">{history.status}</span>
                                      <span className="text-xs text-gray-500">{formatDateTime(history.updated_at)}</span>
                                    </div>
                                    {history.note && (
                                      <p className="text-gray-600 font-body">{history.note}</p>
                                    )}
                                    <p className="text-xs text-gray-400 mt-1">بواسطة: {history.updated_by_name}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* القضايا الرسمية */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-lg font-heading font-bold text-primary mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-gold" />
                قضاياي ({filteredCases.length})
              </h3>
              {filteredCases.length > 0 ? (
                <div className="space-y-3">
                  {filteredCases.map((caseItem) => (
                    <div key={caseItem.id} className="bg-sand rounded-lg overflow-hidden">
                      <div 
                        className="p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => toggleCase(caseItem.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-gold font-semibold font-body">{caseItem.case_number || '-'}</span>
                              <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded font-body">
                                {caseItem.case_type}
                              </span>
                            </div>
                            <h4 className="font-heading font-semibold text-primary">{caseItem.title}</h4>
                            {caseItem.last_action && (
                              <p className="text-sm text-gray-600 mt-1 font-body">
                                <Clock className="w-3 h-3 inline ml-1" />
                                آخر إجراء: {caseItem.last_action}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(caseItem.status)}
                            {expandedCase === caseItem.id ? (
                              <ChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {expandedCase === caseItem.id && (
                        <div className="border-t border-gray-200 p-4 bg-white">
                          <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                            <div>
                              <span className="text-gray-500">المدعي:</span>
                              <span className="mr-2 font-body">{caseItem.plaintiff_name || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">المدعى عليه:</span>
                              <span className="mr-2 font-body">{caseItem.defendant_name || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">المحكمة:</span>
                              <span className="mr-2 font-body">{caseItem.court_name || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">تاريخ الجلسة:</span>
                              <span className="mr-2 font-body">{caseItem.hearing_date || '-'}</span>
                            </div>
                          </div>
                          
                          {caseUpdates[caseItem.id]?.length > 0 && (
                            <div>
                              <h5 className="font-semibold text-primary mb-2 flex items-center gap-1">
                                <Eye className="w-4 h-4" />
                                سجل التحديثات
                              </h5>
                              <div className="space-y-2">
                                {caseUpdates[caseItem.id].map((update, idx) => (
                                  <div key={idx} className="bg-sand p-2 rounded text-sm">
                                    <p className="font-body">{update.update_text}</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      {update.updated_by_name} - {formatDateTime(update.created_at)}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-6 font-body">لا توجد قضايا مسجلة</p>
              )}
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-lg font-heading font-bold text-primary mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-gold" />
                استشاراتي ({filteredConsultations.length})
              </h3>
              {filteredConsultations.length > 0 ? (
                <div className="space-y-3">
                  {filteredConsultations.map((c) => (
                    <div key={c.id} className="p-4 bg-sand rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <span className="text-gold font-semibold font-body text-sm">
                            {c.consultation_number || '-'}
                          </span>
                          <h4 className="font-heading font-semibold text-primary">{c.subject}</h4>
                        </div>
                        {getStatusBadge(c.status)}
                      </div>
                      <p className="text-sm text-gray-600 font-body mb-2">{c.message}</p>
                      {c.response && (
                        <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-sm font-medium text-green-700 mb-1">الرد:</p>
                          <p className="text-sm text-green-800 font-body">{c.response}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-6 font-body">لا توجد استشارات مسجلة</p>
              )}
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-lg font-heading font-bold text-primary mb-4 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-gold" />
                معاملاتي ({filteredAssignments.length})
              </h3>
              {filteredAssignments.length > 0 ? (
                <div className="space-y-3">
                  {filteredAssignments.map((a) => (
                    <div key={a.id} className="p-4 bg-sand rounded-lg flex items-center justify-between">
                      <div>
                        <span className="text-gold font-semibold font-body">{a.assignment_number || '-'}</span>
                        <p className="text-sm text-gray-600 font-body">{a.instructions}</p>
                        {a.last_action && (
                          <p className="text-xs text-gray-500 mt-1">آخر إجراء: {a.last_action}</p>
                        )}
                      </div>
                      {getStatusBadge(a.status)}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-6 font-body">لا توجد معاملات مسجلة</p>
              )}
            </div>
          </div>
        )}

        {/* =============== Consultation Form =============== */}
        {activeView === 'consultation' && (
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gold/20 rounded-lg">
                <MessageSquare className="w-6 h-6 text-gold" />
              </div>
              <div>
                <h3 className="text-xl font-heading font-bold text-primary">طلب استشارة قانونية</h3>
                <p className="text-sm text-gray-500 font-body">سيقوم أحد محامينا بالرد عليك في أقرب وقت</p>
              </div>
            </div>

            <form onSubmit={submitConsultation} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-primary mb-2 font-body">
                  موضوع الاستشارة <span className="text-red-500">*</span>
                </label>
                <input
                  data-testid="consultation-subject-input"
                  type="text"
                  value={consultationForm.subject}
                  onChange={(e) => setConsultationForm({ ...consultationForm, subject: e.target.value })}
                  required
                  placeholder="مثال: استشارة حول عقد عمل"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold outline-none font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2 font-body">
                  تفاصيل الاستشارة <span className="text-red-500">*</span>
                </label>
                <textarea
                  data-testid="consultation-message-input"
                  value={consultationForm.message}
                  onChange={(e) => {
                    if (e.target.value.length <= 2000) {
                      setConsultationForm({ ...consultationForm, message: e.target.value });
                    }
                  }}
                  required
                  rows={8}
                  maxLength={2000}
                  placeholder="اشرح استفسارك بالتفصيل..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold outline-none font-body"
                />
                <p className="text-xs text-gray-500 mt-1 font-body text-left">
                  {consultationForm.message.length}/2000 حرف
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2 font-body">
                  رقم الجوال <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    value={consultationForm.phone_number}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setConsultationForm({ ...consultationForm, phone_number: val });
                    }}
                    required
                    placeholder="05XXXXXXXX"
                    maxLength={10}
                    className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold outline-none font-body text-left"
                    dir="ltr"
                    data-testid="consultation-phone-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1 font-body">
                  {consultationForm.phone_number.length}/10 أرقام
                  {consultationForm.phone_number.length === 10 && <span className="text-green-600 mr-2">✓</span>}
                </p>
              </div>

              <FileAttachments 
                files={consultationFiles}
                setFiles={setConsultationFiles}
                fileRef={consultationFileRef}
              />

              <button
                type="submit"
                disabled={submitLoading}
                data-testid="submit-consultation-btn"
                className="w-full py-4 bg-gold text-white rounded-lg font-bold hover:bg-gold-dark transition-all disabled:opacity-50 font-body"
              >
                {submitLoading ? 'جاري الإرسال...' : 'إرسال طلب الاستشارة'}
              </button>
            </form>
          </div>
        )}

        {/* =============== Notary Services Form =============== */}
        {activeView === 'notary' && (
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-purple-100 rounded-lg">
                <PenTool className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-xl font-heading font-bold text-primary">خدمات الموثق</h3>
                <p className="text-sm text-gray-500 font-body">توثيق العقود والمستندات الرسمية</p>
              </div>
            </div>

            <form onSubmit={submitNotaryRequest} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-primary mb-2 font-body">
                  نوع الخدمة <span className="text-red-500">*</span>
                </label>
                <select
                  data-testid="notary-service-select"
                  value={notaryForm.service_type}
                  onChange={(e) => setNotaryForm({ ...notaryForm, service_type: e.target.value, city_name: '' })}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold outline-none font-body"
                >
                  <option value="">اختر نوع الخدمة</option>
                  {notaryServices.map((service) => (
                    <option key={service} value={service}>{service}</option>
                  ))}
                </select>
              </div>

              {/* حقل اسم المدينة للخدمات العقارية */}
              {propertyServices.includes(notaryForm.service_type) && (
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 font-body">
                    اسم المدينة التي يقع العقار بها <span className="text-red-500">*</span>
                  </label>
                  <input
                    data-testid="notary-city-input"
                    type="text"
                    value={notaryForm.city_name}
                    onChange={(e) => setNotaryForm({ ...notaryForm, city_name: e.target.value })}
                    required
                    placeholder="مثال: الرياض، جدة، الدمام..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold outline-none font-body"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-primary mb-2 font-body">
                  تفاصيل الطلب <span className="text-red-500">*</span>
                </label>
                <textarea
                  data-testid="notary-description-input"
                  value={notaryForm.description}
                  onChange={(e) => setNotaryForm({ ...notaryForm, description: e.target.value })}
                  required
                  rows={5}
                  placeholder="اشرح تفاصيل الخدمة المطلوبة..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold outline-none font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2 font-body">
                  رقم الجوال <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    value={notaryForm.phone_number}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setNotaryForm({ ...notaryForm, phone_number: val });
                    }}
                    required
                    placeholder="05XXXXXXXX"
                    maxLength={10}
                    className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold outline-none font-body text-left"
                    dir="ltr"
                    data-testid="notary-phone-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1 font-body">
                  {notaryForm.phone_number.length}/10 أرقام
                  {notaryForm.phone_number.length === 10 && <span className="text-green-600 mr-2">✓</span>}
                </p>
              </div>

              <FileAttachments 
                files={notaryFiles}
                setFiles={setNotaryFiles}
                fileRef={notaryFileRef}
              />

              <button
                type="submit"
                disabled={submitLoading}
                data-testid="submit-notary-btn"
                className="w-full py-4 bg-purple-500 text-white rounded-lg font-bold hover:bg-purple-600 transition-all disabled:opacity-50 font-body"
              >
                {submitLoading ? 'جاري الإرسال...' : 'إرسال طلب التوثيق'}
              </button>
            </form>
          </div>
        )}
      </main>

      {/* أيقونات التواصل الثابتة */}
      <div className="fixed bottom-6 left-6 flex flex-col gap-3 z-50">
        <WhatsAppFloatingIcon 
          clientName={user?.full_name}
          context="استفسار من بوابة العميل"
        />
        <button
          onClick={() => {
            const roomName = `alkhayat-client-${Date.now()}`;
            window.open(`https://meet.jit.si/${roomName}`, '_blank');
          }}
          className="w-12 h-12 bg-blue-500 text-white rounded-full shadow-lg hover:bg-blue-600 transition-all hover:scale-110 flex items-center justify-center"
          title="طلب مكالمة فيديو مع المكتب"
          data-testid="video-call-btn"
        >
          <Video className="w-6 h-6" />
        </button>
        <a
          href="tel:966568228974"
          className="w-12 h-12 bg-purple-500 text-white rounded-full shadow-lg hover:bg-purple-600 transition-all hover:scale-110 flex items-center justify-center"
          title="اتصال بالمكتب"
          data-testid="phone-btn"
        >
          <Phone className="w-6 h-6" />
        </a>
      </div>
    </div>
  );
}
